'use client';
import { useEffect, useCallback } from 'react';
import { useOneTrustReady } from '@mentimeter/onetrust';
import { getVisitorDataCache } from '@mentimeter/visitor/visitor-data';
import { billingRules, useSubscriptions } from '@mentimeter/billing';
import { TrackingMethods } from '@mentimeter/google-tracking';
import { getLocalUser } from '@mentimeter/user';
import { trackUser } from '@api/tracking/client';
import { trackVisitor } from 'src/trackVisitor';

const ONETRUST_DOMAIN_SCRIPT = process.env.NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT;
const _user = getLocalUser();

export default function TrackPageView({
  children,
}: { children: React.ReactNode }) {
  const { subscriptions } = useSubscriptions(!_user); // don't cache request for non-authed user
  const { planCategory } = billingRules(subscriptions);

  const trackPageView = useCallback(() => {
    const localUser = getLocalUser();
    const { location } = window;
    const { preferredLanguage, country } = getVisitorDataCache();

    const payload = {
      event: 'Viewed page',
      properties: { Page: location.pathname },
    };

    trackVisitor(payload);

    if (localUser) {
      trackUser(payload);
    }

    TrackingMethods.trackPageView({
      page_location: location.href,
      page_title: document.title,
      country,
      language: preferredLanguage,
      logged_in: !!localUser,
      plan_type: planCategory,
      user_purpose: localUser?.flags.onboarding,
    });
  }, [planCategory]);

  useEffect(() => {
    if (!ONETRUST_DOMAIN_SCRIPT) {
      // backup sending trackPageView in case OneTrust is disabled
      trackPageView();
    }
  }, [trackPageView]);

  // this will trigger when OneTrust is ready and cookie banner is loaded or when cookie categories are changed
  // the reason for that is GTM (and GA) wants us to trigger page view only after we set categories in OneTrust
  // won't trigger if OneTrust is disabled
  useOneTrustReady(trackPageView, { once: false });

  return <div>{children}</div>;
}
